import { useEffect, useState } from "react";
import { deleteQuotesAndArticles } from "../api";
import { Toast } from "src/components";

const SortableColumns = {
    quoteImage: 1,
    quoteText: 2,
    articleImage: 3,
    articleTitle: 4,
    articleDescription: 5,
};

export const useQuotesTableHooks = (data, setQuotesFromParent) => {
    const [quotes, setQuotes] = useState([]);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [selectAllQuotes, setSelectAllQuotes] = useState(false);
    const [selectedQuotes, setSelectedQuotes] = useState([]);

    useEffect(() => {
        setQuotes(data);
    }, [data]);

    const handleSearchQuotes = (e) => {
        const value = e.target.value;
        if (!value) return setQuotes(data);
        setQuotes(
            data.filter(quote =>
                quote.quoteText.toLowerCase().includes(value.toLowerCase()) ||
                quote?.articleTitle?.toLowerCase().includes(value.toLowerCase())
            )
        );
    };

    const handleSortColumn = (columnIndex) => {
        if (!quotes.length) return;

        let fieldName;
        switch (columnIndex) {
            case SortableColumns.quoteImage:
                fieldName = "quoteImage";
                break;
            case SortableColumns.quoteText:
                fieldName = "quoteText";
                break;
            case SortableColumns.articleImage:
                fieldName = "articleImage";
                break;
            case SortableColumns.articleTitle:
                fieldName = "articleTitle";
                break;
            case SortableColumns.articleDescription:
                fieldName = "articleDescription";
                break;
            default:
                return;
        }

        const newSortDirection = (sortColumn === columnIndex && sortDirection === "asc") ? "desc" : "asc";
        setSortColumn(columnIndex);
        setSortDirection(newSortDirection);

        const sortedquotes = [...quotes].sort((a, b) => {
            const valueA = a[fieldName] ?? "";
            const valueB = b[fieldName] ?? "";
            const comparison = valueA.localeCompare(valueB);
            return newSortDirection === "asc" ? comparison : -comparison;
        });

        setQuotes(sortedquotes);
    };

    const handleMainCheckbox = () => {
        const newSelectAll = !selectAllQuotes;
        setSelectAllQuotes(newSelectAll);

        if (newSelectAll) {
            setSelectedQuotes(quotes);
        } else {
            setSelectedQuotes([]);
        }
    };

    const toggleQuoteSelection = (quote) => {
        setSelectedQuotes(prev => {
            const isAlreadySelected = prev.some(q => q.uuid === quote.uuid);
            if (isAlreadySelected) {
                return prev.filter(q => q.uuid !== quote.uuid);
            } else {
                return [...prev, quote];
            }
        });
    };

    const isQuoteSelected = (uuid) =>
        selectedQuotes.some(u => u.uuid === uuid);
    
    const handleDeleteSelectedQuotes = async () => {
        const selectedUUIDs = [...selectedQuotes.map(user => user.uuid)];

        setSelectedQuotes([]);
        setSelectAllQuotes(false);

        for (const uuid of selectedUUIDs) {
            try {
                const response = await deleteQuotesAndArticles(uuid);

                if (response.info === "success") {
                    setQuotes(prevquotes => prevquotes.filter(quote => quote.uuid !== uuid));
                    setQuotesFromParent(prevquotes => prevquotes.filter(quote => quote.uuid !== uuid));
                    Toast.success("Quote deleted successfully");
                } 
            } catch (error) {
                console.error(`Error deleting quote ${uuid}:`, error);
                Toast.error(`Error deleting quote: ${uuid}`);
            }
        }
    };

    // Added for select all, then deselect specific quotes
    useEffect(() => {
        if (quotes.length === 0) return;
        const allSelected = quotes.length > 0 && quotes.every(q =>
            selectedQuotes.some(sq => sq.uuid === q.uuid)
        );
        setSelectAllQuotes(allSelected);
    }, [selectedQuotes, quotes]);

    return {
        quotes,
        sortColumn,
        sortDirection,
        selectedQuotes,
        selectAllQuotes,
        handleSearchQuotes,
        handleMainCheckbox,
        toggleQuoteSelection,
        isQuoteSelected,
        setQuotes,
        setSortColumn,
        setSortDirection,
        handleSortColumn,
        handleDeleteSelectedQuotes,
    };
};

